import { Link, useParams } from "react-router-dom";
import { Car, ClipboardCheck, FileText, MessageSquare, Receipt, Wrench } from "lucide-react";
import { Card, SectionTitle, LoadingState, ErrorState, StatusBadge } from "@/components/common";
import { RecordHeader, SmartButton, SmartButtons } from "@/components/layout/Record";
import { StaffHistoryReport } from "@/modules/connect/HistoryReport";
import { MessageCustomerButton } from "@/modules/messaging/Inbox";
import { Thread } from "@/modules/messaging/Thread";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { fmtDateTime, fmtNumber } from "@/lib/format";
import { t } from "@/i18n";

const ALL_JOBS = "booked,received,inspecting,awaiting_approval,approved,in_progress,ready,completed,cancelled";
const INSPECTED = ["awaiting_approval", "approved", "in_progress", "ready", "completed"];

function Conversation({ vid }) {
  const { garage, readOnly, hasFeature } = useGarage();
  const conv = useFetch(hasFeature("messaging") ? `/messages/by-vehicle/${vid}` : null);
  if (!hasFeature("messaging")) return null;
  return (
    <Card testId="vehicle-conversation">
      <SectionTitle>{t("msg.title")}</SectionTitle>
      {conv.data?.id ? <div className="-mx-2 h-[420px]"><Thread path={`/messages/conversations/${conv.data.id}`} sendPath={`/messages/conversations/${conv.data.id}/messages`} tz={garage.timezone} readOnly={readOnly} /></div>
        : <div className="space-y-3 text-sm text-slate-500"><p>{t("b13.vehicle.noConversation")}</p><MessageCustomerButton vehicleId={vid} /></div>}
    </Card>
  );
}

function Activity({ jobs, tz }) {
  return (
    <Card testId="vehicle-activity">
      <SectionTitle>{t("b13.vehicle.activity")}</SectionTitle>
      {jobs.length === 0 ? <p className="text-sm text-slate-500">{t("b13.vehicle.noActivity")}</p> : (
        <ol className="relative space-y-4 border-l border-slate-200 pl-5">
          {jobs.slice(0, 12).map((j) => (
            <li key={j.id} className="relative">
              <span className="absolute -left-[25px] top-1.5 h-2.5 w-2.5 rounded-full bg-teal-500 ring-4 ring-white" />
              <Link to={`/pro/jobs/${j.id}`} className="block text-sm hover:underline" data-testid={`vehicle-activity-${j.number}`}><strong className="font-mono">{j.number}</strong> · {j.reason || "—"}</Link>
              <span className="mt-1 flex items-center gap-2 text-xs text-slate-500"><StatusBadge status={j.status} />{fmtDateTime(j.updated_at, tz)}</span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

export default function VehicleDetail() {
  const { id } = useParams();
  const { garage, isManager, hasFeature } = useGarage();
  const billing = hasFeature("quotes_and_invoices");
  const { data: v, error, loading, reload } = useFetch(`/vehicles/${id}`);
  const cust = useFetch(v ? `/customers/${v.customer_id}` : null);
  const jobs = useFetch("/jobs", { vehicle_id: id, status: ALL_JOBS });
  const quotes = useFetch(billing && v ? "/quotes" : null, { customer_id: v?.customer_id });
  const unpaid = useFetch(billing && isManager && v ? "/invoices" : null, { status: "unpaid", customer_id: v?.customer_id });
  if (loading && !v) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const js = jobs.data || [];
  const mine = (rows) => rows && rows.filter((r) => !r.vehicle?.id || r.vehicle.id === id);
  const c = cust.data;
  return (
    <div className="space-y-6" data-testid="vehicle-detail">
      <RecordHeader crumbs={[[t("b13.nav.customers"), "/pro/customers"], [t("pro.nav.vehicles"), "/pro/vehicles"], [v.plate]]}
        eyebrow={<span className="inline-block rounded-md border-2 border-slate-800 bg-yellow-300 px-2 font-mono-plate text-slate-900" data-testid="vehicle-plate">{v.plate}</span>}
        title={`${v.make} ${v.model}`} subtitle={c && <Link to={`/pro/customers/${c.id}`} className="font-medium text-teal-700 hover:underline" data-testid="vehicle-owner-link">{c.first_name} {c.last_name}</Link>}
        actions={<><StaffHistoryReport vehicle={v} /><MessageCustomerButton vehicleId={v.id} /></>}
        smart={<SmartButtons>
          <SmartButton icon={Wrench} label={t("jobs.title")} value={js.length} to={`/pro/jobs?status=all&vehicle_id=${id}`} testId="smart-jobs" />
          {billing && <SmartButton icon={FileText} label={t("quotes.title")} value={mine(quotes.data)?.length} to="/pro/quotes" testId="smart-quotes" />}
          {billing && isManager && <SmartButton icon={Receipt} label={t("b13.smart.unpaid")} value={mine(unpaid.data)?.length} to={`/pro/invoices?status=unpaid&customer_id=${v.customer_id}`} testId="smart-unpaid" tone={mine(unpaid.data)?.length ? "text-amber-700" : ""} />}
          <SmartButton icon={ClipboardCheck} label={t("b13.nav.inspections")} value={js.filter((j) => INSPECTED.includes(j.status)).length} to={`/pro/jobs?status=all&vehicle_id=${id}`} testId="smart-inspections" />
          {hasFeature("messaging") && <SmartButton icon={MessageSquare} label={t("msg.title")} value={t("b13.smart.open")} to={`/pro/messages?vehicle=${id}`} testId="smart-messages" />}
        </SmartButtons>} />
      <div className="grid gap-6 xl:grid-cols-[1.3fr_1fr] [&>*]:min-w-0">
        <div className="space-y-6">
          <Card testId="vehicle-info-card">
            <SectionTitle>{t("b13.vehicle.details")}</SectionTitle>
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              {[["vehicles.make", v.make], ["vehicles.model", v.model], ["vehicles.year", v.year], ["vehicles.vin", v.vin], ["jobs.lastOdometer", v.odometer != null ? `${fmtNumber(v.odometer)} km` : null], ["b13.vehicle.notes", v.notes]]
                .map(([k, val]) => <div key={k}><dt className="text-slate-500">{t(k)}</dt><dd className="font-medium">{val || "—"}</dd></div>)}
            </dl>
          </Card>
          <Card testId="vehicle-jobs-card">
            <SectionTitle>{t("jobs.title")}</SectionTitle>
            {js.length === 0 ? <p className="text-sm text-slate-500">{t("jobs.empty")}</p> : (
              <div className="divide-y divide-slate-100">{js.map((j) => (
                <Link key={j.id} to={`/pro/jobs/${j.id}`} className="flex items-center gap-3 py-3 text-sm hover:bg-slate-50" data-testid={`vehicle-job-${j.number}`}>
                  <Car className="h-4 w-4 text-slate-400" /><span className="font-mono font-semibold">{j.number}</span><span className="min-w-0 flex-1 truncate text-slate-600">{j.reason}</span><StatusBadge status={j.status} />
                </Link>))}</div>
            )}
          </Card>
        </div>
        <div className="space-y-6"><Conversation vid={id} /><Activity jobs={js} tz={garage.timezone} /></div>
      </div>
    </div>
  );
}
